import _ from 'lodash';
import {createSelector} from 'reselect';

import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {isIntegrationTypeEnabled} from '../../../helpers/integrationTypesHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {IntegrationTypesSupportedTypesEnum} from '../../../models/api/integrationTypesModel';
import {AppState} from '../../appState';
import {AsyncStatus, ResourceTableStatusesEnum} from '../../appTypes';
import {getIntegrationsByType} from '../dataSelectors';

/*
 * Base Integration Types.
 */

export function getIntegrationTypes(state: AppState) {
  return state.data.integrationTypes.byId;
}

export const getFlatIntegrationTypes = createSelector(getIntegrationTypes, (integrationTypes) =>
  _(integrationTypes).values().compact().value(),
);

export const getAllIntegrationTypes = createSelector(getFlatIntegrationTypes, (integrationTypes) =>
  _(integrationTypes)
    .sortBy((t) => t.name)
    .value(),
);

export const getAlphabeticalSortedIntegrationTypes = createSelector(
  getFlatIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .sort((a, b) => compareName(a.name.toLowerCase(), b.name.toLowerCase()))
      .value(),
);

export const getFlatPopularIntegrationTypes = createSelector(
  getAlphabeticalSortedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) => integrationType.popularity !== 0)
      .sort((a, b) => b.popularity - a.popularity)
      .value(),
);

export const getFlatRecentlyAddedIntegrationTypes = createSelector(
  getAlphabeticalSortedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .sort((a, b) => b.launchDate - a.launchDate)
      .value(),
);

/*
 * Integration Enabled Selector.
 */

export function makeGetIsIntegrationTypeEnabled(
  getIntegrationTypeId: AppParametricSelector<any, string | undefined>,
) {
  return createSelector(
    [getIntegrationTypeId, getIntegrationTypes, getIntegrationsByType],
    (integrationId, integrationTypes, integrations) => {
      if (!integrationId) {
        return false;
      }

      // Try to fetch the integration and integration types.
      const integrationType = integrationTypes[integrationId];
      const integration = integrations[integrationId];

      if (isIntegrationTypeEnabled(integrationType)) {
        return true;
      }

      // If we have an integration, check to see if it is enabled.
      if (integration) {
        return integration.isEnabled;
      }

      // By default we should not render it enabled.
      return false;
    },
  );
}

/*
 * Integration Type Status Selectors.
 */

export function getIntegrationTypesAsyncStatus(state: AppState): AsyncStatus {
  return state.data.integrationTypes;
}

export const getIsIntegrationTypesLoading = createSelector(
  getIntegrationTypesAsyncStatus,
  (status) =>
    status.status === ResourceTableStatusesEnum.INITIAL ||
    status.status === ResourceTableStatusesEnum.LOADING,
);

export const getShouldFetchIntegrationTypes = createSelector(
  getIntegrationTypesAsyncStatus,
  ({isStale, status}) => {
    // If the store is marked as stale, we should refresh.
    if (isStale) {
      return true;
    }
    // If the status is either failed or initial state, we should fetch.
    if (status === ResourceTableStatusesEnum.INITIAL || status === ResourceTableStatusesEnum.FAILED) {
      return true;
    }
    // We can just rely on the redux state currently.
    return false;
  },
);

/*
 * Plugins.
 */

export const getPluginIntegrationTypes = createSelector(
  getAlphabeticalSortedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN),
      )
      .keyBy('id')
      .value(),
);

export const getFlatPluginIntegrationTypes = createSelector(
  getAlphabeticalSortedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN),
      )
      .value(),
);

export const getFlatPopularPluginIntegrationTypes = createSelector(
  getFlatPopularIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN),
      )
      .value(),
);

export const getFlatRecentlyAddedPluginIntegrationTypes = createSelector(
  getFlatRecentlyAddedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.PLUGIN),
      )
      .value(),
);

/*
 * Channels.
 */

export const getFlatPopularChannelIntegrationTypes = createSelector(
  getFlatPopularIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL),
      )
      .value(),
);

export const getFlatChannelIntegrationTypes = createSelector(
  getAlphabeticalSortedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL),
      )
      .value(),
);

export const getFlatRecentlyAddedChannelIntegrationTypes = createSelector(
  getFlatRecentlyAddedIntegrationTypes,
  (integrationTypes) =>
    _([...integrationTypes])
      .filter((integrationType) =>
        integrationType.supportedTypes.includes(IntegrationTypesSupportedTypesEnum.CHANNEL),
      )
      .value(),
);
